import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  Route,
  X,
  Flag,
  MapPin,
  Search as SearchIcon,
  Trash2,
  ExternalLink,
  Loader2,
  ArrowUp,
  ArrowDown,
  QrCode,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import DepotMap from "@/components/DepotMap";
import { useTripCart } from "@/lib/tripCart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_STOPS = 25;

export default function TripPlanner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, add, remove, clear, setItems } = useTripCart();

  const [startId, setStartId] = useState(null);
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [routeGeom, setRouteGeom] = useState(null); // {polyline, road_snapped, distance_miles, duration_minutes}

  // If `?stops=id1,id2,id3` is present, load those depots into the cart (replace).
  // This makes trips shareable via URL and QR code.
  useEffect(() => {
    const raw = searchParams.get("stops");
    if (!raw) return;
    const ids = raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 25);
    if (ids.length < 2) {
      // strip the param and bail
      setSearchParams({}, { replace: true });
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/depots`, { params: { ids: ids.join(","), limit: 50 } })
      .then((r) => {
        if (cancelled) return;
        const byId = Object.fromEntries((r.data || []).map((d) => [d.id, d]));
        const ordered = ids.map((id) => byId[id]).filter(Boolean);
        if (ordered.length >= 2) {
          setItems(
            ordered.map((d) => ({
              id: d.id,
              name: d.name,
              city: d.city,
              state_code: d.state_code,
              latitude: d.latitude,
              longitude: d.longitude,
            }))
          );
          toast.success(`Loaded ${ordered.length}-stop shared trip`);
        }
        setSearchParams({}, { replace: true });
      })
      .catch(() => {
        toast.error("Could not load shared trip");
        setSearchParams({}, { replace: true });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Search-to-add
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (search.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const r = await axios.get(`${API}/depots`, {
          params: { search: search.trim(), limit: 10 },
        });
        if (!cancelled) setSearchResults(r.data || []);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search]);

  const optimize = async () => {
    if (items.length < 2) {
      toast.error("Add at least 2 stops to plan a trip");
      return;
    }
    try {
      setPlanning(true);
      const body = { depot_ids: items.map((i) => i.id) };
      if (startId) body.start_id = startId;
      const r = await axios.post(`${API}/trips/plan`, body);
      setPlan(r.data);
      // reorder the cart to the optimized order so the sidebar reflects the plan
      const ordered = r.data.ordered_depot_ids
        .map((id) => items.find((i) => i.id === id))
        .filter(Boolean);
      if (ordered.length === items.length) setItems(ordered);
      toast.success(
        `Route optimized · ${r.data.stops.length} stops · ~${Math.round(
          r.data.total_miles_crow_flies
        )} mi`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to plan trip");
    } finally {
      setPlanning(false);
    }
  };

  const openInGoogleMaps = () => {
    if (plan?.google_maps_url) {
      window.open(plan.google_maps_url, "_blank", "noopener,noreferrer");
    }
  };

  const move = (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    setPlan(null); // invalidate any prior plan
  };

  const mapPoints = useMemo(() => items, [items]);
  const mapCenter = items[0]
    ? [items[0].latitude, items[0].longitude]
    : [39.8283, -98.5795];

  // Straight-line fallback (used while road geometry loads / if OSRM is down).
  const crowFliesPolyline = useMemo(
    () => items.map((s) => [s.latitude, s.longitude]),
    [items]
  );

  // Fetch a road-snapped polyline whenever the ordered stops change.
  useEffect(() => {
    if (items.length < 2) {
      setRouteGeom(null);
      return;
    }
    let cancelled = false;
    const key = items.map((i) => i.id).join(",");
    axios
      .post(`${API}/trips/route-geometry`, { depot_ids: items.map((i) => i.id) })
      .then((r) => {
        if (cancelled) return;
        // Guard against stale response (user re-ordered while request in flight)
        const stillCurrentKey = items.map((i) => i.id).join(",");
        if (stillCurrentKey !== key) return;
        setRouteGeom(r.data);
      })
      .catch(() => {
        if (!cancelled) setRouteGeom(null);
      });
    return () => {
      cancelled = true;
    };
  }, [items]);

  const routePolyline = routeGeom?.polyline || crowFliesPolyline;

  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5 bg-[#0a0a0a]/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/5"
              data-testid="trip-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <h1 className="font-serif font-bold text-lg text-white flex items-center gap-2">
                <Route className="w-5 h-5 text-amber-500" />
                Plan a Depot Road Trip
              </h1>
              <p className="text-xs text-white/50">
                Pick up to {MAX_STOPS} depots · we&apos;ll optimize the route
              </p>
            </div>
            {items.length > 0 && (
              <Button
                variant="ghost"
                onClick={() => {
                  clear();
                  setPlan(null);
                  setStartId(null);
                }}
                className="text-white/60 hover:text-red-400 hover:bg-white/5"
                data-testid="trip-clear-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: stops list + search */}
          <div className="lg:col-span-1 space-y-5">
            {/* Search-to-add */}
            <Card className="bg-[#111] border-white/5" data-testid="trip-search-card">
              <CardContent className="p-4">
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 block">
                  Add a stop
                </label>
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search depots by name or city..."
                    className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="trip-search-input"
                  />
                </div>
                {searching && (
                  <p className="text-xs text-white/40 mt-2">Searching…</p>
                )}
                {searchResults.length > 0 && (
                  <div className="mt-3 space-y-1 max-h-64 overflow-y-auto">
                    {searchResults.map((d) => {
                      const already = items.some((i) => i.id === d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          disabled={already || items.length >= MAX_STOPS}
                          onClick={() => {
                            add(d);
                            setPlan(null);
                            setSearch("");
                            setSearchResults([]);
                            toast.success(`Added ${d.name}`);
                          }}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between gap-2 ${
                            already
                              ? "bg-emerald-500/10 text-emerald-400/70 cursor-default"
                              : "hover:bg-amber-500/10 text-white/85"
                          }`}
                          data-testid={`trip-search-result-${d.id}`}
                        >
                          <span className="min-w-0 truncate">
                            <span className="font-medium">{d.name}</span>
                            <span className="text-white/40 ml-1.5">
                              · {d.city}, {d.state_code}
                            </span>
                          </span>
                          {already ? (
                            <span className="text-[10px] uppercase tracking-wider text-emerald-400 shrink-0">
                              Added
                            </span>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wider text-amber-500 shrink-0">
                              + Add
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stops list */}
            <Card className="bg-[#111] border-white/5" data-testid="trip-stops-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-500" />
                    Stops
                  </h3>
                  <Badge variant="secondary" className="bg-white/5 text-white/70 border-white/10">
                    {items.length}/{MAX_STOPS}
                  </Badge>
                </div>

                {items.length === 0 ? (
                  <div className="py-8 text-center">
                    <Route className="w-10 h-10 mx-auto mb-3 text-white/10" />
                    <p className="text-sm text-white/50 mb-1">
                      No stops yet.
                    </p>
                    <p className="text-xs text-white/40">
                      Search above, or browse the{" "}
                      <Link to="/" className="text-amber-500 hover:underline">
                        main map
                      </Link>{" "}
                      and tap &quot;Add to trip&quot;.
                    </p>
                  </div>
                ) : (
                  <ol className="space-y-2">
                    {items.map((stop, idx) => {
                      const isStart = startId === stop.id || (idx === 0 && !startId);
                      return (
                        <li
                          key={stop.id}
                          className="group flex items-start gap-2 px-2.5 py-2 rounded-md bg-white/[0.03] border border-white/5 hover:border-amber-500/30"
                          data-testid={`trip-stop-${stop.id}`}
                        >
                          <span className="w-6 h-6 rounded-full bg-amber-500 text-black text-xs font-bold flex items-center justify-center shrink-0 mt-0.5 font-mono">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">
                              {stop.name}
                            </p>
                            <p className="text-xs text-white/40">
                              {stop.city}, {stop.state_code}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <div className="flex gap-1">
                              <button
                                onClick={() => move(idx, -1)}
                                disabled={idx === 0}
                                title="Move up"
                                className="text-white/40 hover:text-amber-500 disabled:opacity-20 disabled:hover:text-white/40"
                                data-testid={`trip-move-up-${stop.id}`}
                              >
                                <ArrowUp className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => move(idx, +1)}
                                disabled={idx === items.length - 1}
                                title="Move down"
                                className="text-white/40 hover:text-amber-500 disabled:opacity-20 disabled:hover:text-white/40"
                                data-testid={`trip-move-down-${stop.id}`}
                              >
                                <ArrowDown className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => {
                                  remove(stop.id);
                                  if (startId === stop.id) setStartId(null);
                                  setPlan(null);
                                }}
                                title="Remove"
                                className="text-white/40 hover:text-red-400"
                                data-testid={`trip-remove-${stop.id}`}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <button
                              onClick={() => {
                                setStartId(isStart ? null : stop.id);
                                setPlan(null);
                              }}
                              className={`text-[10px] uppercase tracking-wider inline-flex items-center gap-1 ${
                                isStart
                                  ? "text-amber-500 font-semibold"
                                  : "text-white/40 hover:text-amber-500"
                              }`}
                              title="Set as starting point"
                              data-testid={`trip-set-start-${stop.id}`}
                            >
                              <Flag className="w-3 h-3" />
                              {isStart ? "Start" : "Set start"}
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="bg-[#111] border-white/5">
              <CardContent className="p-4 space-y-3">
                <Button
                  onClick={optimize}
                  disabled={items.length < 2 || planning}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black"
                  data-testid="trip-optimize-btn"
                >
                  {planning ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Optimizing…
                    </>
                  ) : (
                    <>
                      <Route className="w-4 h-4 mr-2" />
                      Optimize Route
                    </>
                  )}
                </Button>
                {plan && (
                  <>
                    <Button
                      onClick={openInGoogleMaps}
                      variant="outline"
                      className="w-full border-white/10 text-white hover:bg-white/5"
                      data-testid="trip-open-gmaps-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open in Google Maps
                    </Button>
                    <Button
                      onClick={() => setShowQR((v) => !v)}
                      variant="outline"
                      className="w-full border-white/10 text-white hover:bg-white/5"
                      data-testid="trip-show-qr-btn"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      {showQR ? "Hide QR code" : "Scan to phone (QR)"}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: map + result summary */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="bg-[#111] border-white/5" data-testid="trip-map-card">
              <CardContent className="p-0 overflow-hidden rounded-xl">
                <div className="h-[420px] md:h-[560px]">
                  <DepotMap
                    depots={mapPoints}
                    towers={[]}
                    onDepotClick={() => {}}
                    center={mapCenter}
                    zoom={mapPoints.length > 0 ? 5 : 4}
                    darkMode={true}
                    showDepots={true}
                    showTowers={false}
                    autoFit={mapPoints.length > 0 ? "always" : false}
                    routePolyline={routePolyline}
                  />
                </div>
              </CardContent>
            </Card>

            {plan && (
              <Card className="bg-[#111] border-white/5" data-testid="trip-summary-card">
                <CardContent className="p-5">
                  <div className="flex items-baseline justify-between mb-4">
                    <h3 className="font-serif text-lg text-white flex items-center gap-2">
                      <Route className="w-4 h-4 text-amber-500" />
                      Optimized Route
                    </h3>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">
                      {plan.algorithm === "brute_force_tsp"
                        ? "Optimal · brute-force TSP"
                        : "Heuristic · nearest-neighbor"}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="bg-white/[0.03] rounded-md px-3 py-2.5 border border-white/5">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                        Stops
                      </p>
                      <p className="font-mono text-white text-xl mt-0.5">
                        {plan.stops.length}
                      </p>
                    </div>
                    <div className="bg-white/[0.03] rounded-md px-3 py-2.5 border border-white/5">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                        {routeGeom?.road_snapped ? "Driving distance" : "Total (crow-flies)"}
                      </p>
                      <p className="font-mono text-amber-400 text-xl mt-0.5">
                        {routeGeom?.road_snapped
                          ? `${Math.round(routeGeom.distance_miles).toLocaleString()} mi`
                          : `${Math.round(plan.total_miles_crow_flies).toLocaleString()} mi`}
                      </p>
                      {routeGeom?.road_snapped && routeGeom.duration_minutes && (
                        <p className="text-[10px] text-white/50 mt-0.5 font-mono">
                          ≈ {Math.floor(routeGeom.duration_minutes / 60)}h {Math.round(routeGeom.duration_minutes % 60)}m drive
                        </p>
                      )}
                    </div>
                  </div>

                  <ol className="space-y-1.5 mb-2">
                    {plan.stops.map((s, i) => (
                      <li
                        key={s.id}
                        className="flex items-baseline gap-2 text-sm"
                        data-testid={`trip-plan-row-${i}`}
                      >
                        <span className="font-mono text-amber-500 w-6 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <Link
                          to={`/depot/${s.id}`}
                          className="text-white/85 hover:text-amber-500 truncate"
                        >
                          {s.name}
                        </Link>
                        <span className="text-white/40 text-xs shrink-0">
                          {s.city}, {s.state_code}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <p className="text-[11px] text-white/40 mt-3 font-mono">
                    {routeGeom?.road_snapped
                      ? "Route follows public roads via OSRM · open in Google Maps for turn-by-turn."
                      : "Straight-line distance shown. Actual driving mileage will be longer — open in Google Maps for turn-by-turn."}
                  </p>

                  {showQR && plan.google_maps_url && (
                    <div
                      className="mt-5 pt-5 border-t border-white/5 flex flex-col sm:flex-row items-center gap-5"
                      data-testid="trip-qr-panel"
                    >
                      <div className="bg-white p-3 rounded-lg shrink-0 shadow-lg shadow-black/40">
                        <QRCodeSVG
                          value={plan.google_maps_url}
                          size={168}
                          level="M"
                          includeMargin={false}
                          fgColor="#0a0a0a"
                          bgColor="#ffffff"
                        />
                      </div>
                      <div className="flex-1 min-w-0 text-center sm:text-left">
                        <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500 mb-1.5 flex items-center gap-1.5 justify-center sm:justify-start">
                          <Smartphone className="w-3.5 h-3.5" />
                          Scan to open on your phone
                        </p>
                        <p className="text-sm text-white/85 mb-3">
                          Point your phone&apos;s camera at the code. Google Maps
                          opens on your device with turn-by-turn directions for
                          all {plan.stops.length} stops.
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard?.writeText(plan.google_maps_url);
                              toast.success("Trip link copied to clipboard");
                            }}
                            className="text-xs px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/85 hover:bg-white/10 hover:border-amber-500/40"
                            data-testid="trip-copy-link-btn"
                          >
                            Copy link
                          </button>
                          <a
                            href={`sms:?body=${encodeURIComponent(
                              `Depot road trip · ${plan.stops.length} stops · ${Math.round(plan.total_miles_crow_flies)} mi\n${plan.google_maps_url}`
                            )}`}
                            className="text-xs px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/85 hover:bg-white/10 hover:border-amber-500/40 inline-block"
                            data-testid="trip-text-link-btn"
                          >
                            Text to phone
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
